import * as kendo from 'kendo/kendo.core';
import * as i18n from 'CMS/core/i18n';
import { LengthUnit } from 'visuris/Helpers/Enums';

// Change the group separator to "." instead of " " for the culture nl-BE
var nlbeCulture = <any>kendo.cultures["nl-BE"];
if (nlbeCulture) {
    (nlbeCulture.numberFormat)[","] = ".";
    kendo.culture("nl-BE");
}

/**
 * Format the given coordinate by rounding it
 * @param coordinate
 */
export function formatCoordinate(coordinate: number): string {
    if (coordinate != null) {
        return (Math.round(coordinate * 100000) / 100000).toString();
    }
    return null;
}

export function formatNumber(input: number): string {
    if (input != null) {
        return kendo.toString(input, "n");
    }
    return null;
}

export function formatPercentage(input: number, fraction: boolean = false, withUnit: boolean = true, numberOfDecimals: number = 2): string {
    let percentage: string = null;
    if (input != null) {
        if (fraction) {
            percentage = kendo.toString(input * 100, "n" + numberOfDecimals.toString());
        } else {
            percentage = kendo.toString(input, "n" + numberOfDecimals.toString());
        }
        if (withUnit) {
            return percentage + " %";
        } else {
            return percentage;
        }
    }
    return null;
}

/**
 * Format the given date to the given format
 * @param date
 * @param format
 */
export function formatDate(date: Date, format: string = "dd/MM/yyyy H:mm"): string {
    if (format == null || date == null || date.getFullYear() < 1971) { return ""; }
    return kendo.toString(date, format);
}

export function formatDateShortenToday(date: Date, format: string = "dd/MM/yyyy H:mm"): string {
    if (format == null || date == null || date.getFullYear() < 1971) { return ""; }
    if (date.getDate() === new Date().getDate() && date.getMonth() === new Date().getMonth() && date.getFullYear() === new Date().getFullYear()) {
        return kendo.toString(date, 'HH:mm');
    }
    return kendo.toString(date, format);
}

export function formatTimeToHours(minutes: number): string {
    var hours = Math.floor(minutes / 60);
    var minutesAfterHour = minutes - (hours * 60);
    return hours.toString() + i18n.getMessage("units.hours") + minutesAfterHour.toString() + i18n.getMessage("units.minutes");
}

/**
 * Show voyage time on direction widget:
 *  - more than 100 days -> 100d+
 *  - more or equals dan 1 day -> 1d 17h or 1d 40m (if zero hours)
 *  - less dan 1 day -> 17h45
 * @param minutes
 * @returns
 */
export function formatTimeToDays(minutes: number): string {
    const days = Math.floor(minutes / 60 / 24);
    const hours = Math.floor(minutes / 60) - (days * 24);
    const minutesAfterHour = minutes - ((hours * 60) + (days * 60 * 24));
    if (days > 100) {
        return `${days.toString()}${i18n.getMessage('units.days')}+`;
    } else if (days >= 1) {
        let ret = `${days.toString()}${i18n.getMessage('units.days')}`;
        if (hours > 0) {
            ret += ` ${hours.toString()}${i18n.getMessage("units.hours")}`;
        } else if (minutesAfterHour > 0) {
            ret += ` ${minutesAfterHour.toString()}${i18n.getMessage("units.minutes")}`;
        }
        return ret;
    } else {
        const minutesWithZero = minutesAfterHour < 10 ? '0' + minutesAfterHour.toString() : minutesAfterHour.toString();
        return `${hours.toString()}${i18n.getMessage("units.hours")}${minutesWithZero}`
    }
}

export function formatLengthAsMeterWithUnitAndDecimals(length: number, lengthUnit: LengthUnit = LengthUnit.Meter): string{
    return formatLengthAsMeter(length, lengthUnit, true, false, true);
}

/**
 * Format the given length as meter, based on the provided length unit (meter or centimeter)
 * @param length
 * @param lengthUnit
 */
export function formatLengthAsMeter(length: number, lengthUnit: LengthUnit = LengthUnit.Meter, withUnit: boolean = true, withoutDecimals: boolean = false, alwaysIncludeDecimals: boolean = false): string {
    length = length > 0 ? length : null;
    let meterFormat: string = null;
    if (length != null) {
        switch (lengthUnit) {
            case LengthUnit.Centimeter:
                let meter = length / 100;
                // don't show the decimals when the meter is round
                if (meter === Math.round(meter) && !alwaysIncludeDecimals) {
                    meterFormat = kendo.toString(meter, "n0");
                } else {
                    meterFormat = kendo.toString(meter, withoutDecimals ? 'n0' : "n2");
                }
                break;
            case LengthUnit.Hectometer:
                meterFormat = kendo.toString(length / 10, "n0");
                break;
            case LengthUnit.Meter:
                meterFormat = kendo.toString(length, withoutDecimals ? 'n0' : "n2");
                break;
        }
        if (withUnit) {
            return meterFormat + " " + i18n.getMessage("units.meters");
        } else {
            return meterFormat;
        }
    }
    return "";
}

/**
 * Format the given length as centimeter, based on the provided length unit (meter or centimeter)
 * @param length
 * @param lengthUnit
 */
export function formatLengthAsCentimeter(length: number, lengthUnit: LengthUnit = LengthUnit.Centimeter, withUnit: boolean = true): string {
    length = length > 0 ? length : null;
    let meterFormat: string = null;
    if (length != null) {
        switch (lengthUnit) {
            case LengthUnit.Centimeter:
                meterFormat = kendo.toString(length, "n0");
                break;
            case LengthUnit.Meter:
                meterFormat = kendo.toString(length * 100, "n0");
                break;
        }
        if (withUnit) {
            return meterFormat + " " + i18n.getMessage("units.centimeter");
        } else {

        } return meterFormat;

    }
    return "";
}

/**
 * Format the given length as meter, based on the provided length unit (meter or centimeter)
 * @param length
 * @param lengthUnit
 */
export function formatLengthAsKilometer(length: number, lengthUnit: LengthUnit = LengthUnit.Meter, withUnit: boolean = true, withoutDecimals: boolean = false): string {
    length = length > 0 ? length : null;
    let kilometerFormat: string = null;
    if (length != null) {
        switch (lengthUnit) {
            case LengthUnit.Centimeter:
                kilometerFormat = kendo.toString(length / 100000, withoutDecimals ? 'n0' : "n5");
                break;
            case LengthUnit.Hectometer:
                kilometerFormat = kendo.toString(length / 10, withoutDecimals ? 'n0' : "n1");
                break;
            case LengthUnit.Meter:
                kilometerFormat = kendo.toString(length / 1000, withoutDecimals ? 'n0' : "n3");
                break;
            case LengthUnit.Kilometer:
                kilometerFormat = kendo.toString(length, withoutDecimals ? 'n0' : "n2");
                break;
        }
        if (withUnit) {
            return kilometerFormat + " " + i18n.getMessage("units.kilometers");
        } else {
            return kilometerFormat;
        }

    }
    return "";
}

/**
 * Format the given length as meter, based on the provided length unit (meter or centimeter)
 * @param length
 * @param lengthUnit
 */
export function formatLengthAsKilometerWithZero(length: number, lengthUnit: LengthUnit = LengthUnit.Meter, withUnit: boolean = true): string {
    let kilometerFormat: string = null;
    if (length != null) {
        if (length == 0) {
            kilometerFormat = kendo.toString(length, "n1")
        } else {
            switch (lengthUnit) {
                case LengthUnit.Centimeter:
                    kilometerFormat = kendo.toString(length / 100000, "n5");
                    break;
                case LengthUnit.Hectometer:
                    kilometerFormat = kendo.toString(length / 10, "n1");
                    break;
                case LengthUnit.Meter:
                    kilometerFormat = kendo.toString(length / 1000, "n3");
                    break;
                case LengthUnit.Kilometer:
                    kilometerFormat = kendo.toString(length, "n2");
                    break;
            }
        }
        if (withUnit) {
            return kilometerFormat + " " + i18n.getMessage("units.kilometers");
        } else {
            return kilometerFormat;
        }

    }
    return "";
}

/**
 * Format the given weight as tonnage
 * @param weight
 */
export function formatWeightAsTonnage(weight: number) {
    return weight != null ? weight.toString() + " " + i18n.getMessage("units.tons") : "";
}

/**
 * Format the given volume as m�
 * @param volume
 */
export function formatVolume(volume: number) {
    return volume != null ? volume.toString() + " m�" : "";
}

export function formatHours(hours: number) {
    return hours != null ? hours.toString() + " " + i18n.getMessage("units.hours") : "";
}

/**
 * Format the given speed to kilometers per hour (unit only, no calculation)
 * @param speed
 */
export function formatSpeed(speed: number): string {
    if (speed == null || speed <= 0) {
        return "";
    }
    return kendo.toString(speed, "n0") + " " + i18n.getMessage("units.kilometersPerHour");
}

export function formatPrice(price: number): string {
    if (price == null) {
        return "";
    }
    return "\u20AC " + kendo.toString(price, "n2");
}